import { getApp, getApps, initializeApp } from 'firebase/app'
import {
  browserLocalPersistence,
  browserPopupRedirectResolver,
  browserSessionPersistence,
  getAuth as fbGetAuth,
  indexedDBLocalPersistence,
  initializeAuth,
  inMemoryPersistence,
  type Persistence,
} from 'firebase/auth'

import { config } from '@/config'
import { getPlatform } from '@/core/native/app'

export type InitProps = {
  appId: string
  projectId: string
  apiKey: string
  senderId: string
  measurementId: string
}

export function init({
  appId,
  projectId,
  apiKey,
  senderId,
  measurementId,
}: InitProps) {
  if (getApps().length) {
    return
  }

  const app = initializeApp({
    appId,
    projectId,
    apiKey,
    authDomain: `${projectId}.firebaseapp.com`,
    databaseURL: `https://${projectId}.firebaseio.com`,
    storageBucket: `${projectId}.appspot.com`,
    messagingSenderId: senderId,
    measurementId,
  })

  // browserLocalPersistence/browserPopupRedirectResolver等はブラウザ専用の実装で、
  // Node.js環境（Next.jsのSSR）で使うと「Expected a class definition」で
  // initializeAuthが同期的に例外を投げる。bootstrapはSSR時にもトップレベルで
  // 実行されるため、ブラウザ実行時のみ初期化するようガードする
  if (typeof window === 'undefined') {
    return
  }

  const isLine = config.platform.includes('line')
  const isNative = getPlatform() !== 'web'

  // LINEミニアプリはiOS WKWebView上で動作し、persistenceの候補に
  // indexedDBLocalPersistenceを含めるだけでプローブ処理がハングし、起動時に
  // 白画面のまま固まる不具合があるため（UT-27404）、IndexedDBに一切触れない
  // 構成にする。それ以外のプラットフォームでは、単一値ではなく配列にすることで
  // 既存のindexedDBLocalPersistenceセッションを起動時に検出しbrowserLocalPersistence
  // へ移行できるようにする（単一値のままだと既存セッションが見えなくなり、
  // サイレントログアウトになる）
  const persistence: Persistence[] = isLine
    ? [browserLocalPersistence, inMemoryPersistence]
    : [
        browserLocalPersistence,
        indexedDBLocalPersistence,
        browserSessionPersistence,
      ]

  initializeAuth(app, {
    persistence,
    // popupRedirectResolverを指定しないとgetRedirectResult()が常にauth/argument-errorを
    // 投げる（SNSログインからのリダイレクトでなくても呼ばれる度に発生する）。
    // getAuth()のデフォルト初期化はこれを自動設定するが、initializeAuth()を明示的に
    // 呼ぶ場合は自分で渡す必要がある。
    //
    // ただしbrowserPopupRedirectResolverを設定すると、_shouldInitProactively
    // （_isMobileBrowser() || _isSafari() || _isIOS()）がtrueになる環境では
    // initializeAuth実行時に認証用iframe/Google APIローダーを先読みする処理が
    // 走ってしまう。LINEミニアプリはこれが原因でAuth初期化中にハングし白画面の
    // まま固まる既知バグ（UT-27404）の当事者だが、ネイティブアプリ（iOS/Android）の
    // WebViewも_isIOS()/_isMobileBrowser()に該当し、同じ先読みが新たに走ってしまう
    // （従来のネイティブ側はpopupRedirectResolverを一切設定していなかったため、
    // この先読み自体が発生していなかった）。通常のWebはgetAuth()のデフォルト初期化が
    // 元々同じ先読みを行っており新規リスクではないため、popupRedirectResolverは
    // Web（LINE以外）のみに限定する
    ...(isLine || isNative
      ? {}
      : { popupRedirectResolver: browserPopupRedirectResolver }),
  })
}

/**
 * Returns the Auth instance associated with the provided @firebase/app#FirebaseApp.
 * If no instance exists throw error
 *
 */
export function getAuth() {
  const app = getApp()
  if (!app) {
    throw new Error('FirebaseApp is not initialized')
  }
  return fbGetAuth(app)
}
